import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Home, MapPin, Search, Send, Users, ShieldCheck } from 'lucide-react';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { formatApiError } from '@/utils/apiError';

const SECTION_LABEL = { probound: 'ProBound', proepoxy: 'ProEpoxy', prodecor: 'ProDecor' };
const SECTION_COLOR = { probound: '#2F5E96', proepoxy: '#7A0B7A', prodecor: '#D4A24B' };

export default function FindInstaller() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const initialLeadId = sp.get('lead_id') || '';

  const [postcode, setPostcode] = useState('');
  const [section, setSection] = useState('probound');
  const [leadId, setLeadId] = useState(initialLeadId);
  const [lead, setLead] = useState(null);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [offering, setOffering] = useState(null);

  // If lead_id is given, pull the lead to pre-fill postcode + section
  useEffect(() => {
    if (!initialLeadId) return;
    fetch(`${API_URL}/api/leads/${initialLeadId}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(l => {
        if (!l) return;
        setLead(l);
        if (l.customer_postcode) setPostcode(l.customer_postcode);
        if (l.service_type) setSection(l.service_type);
      });
  }, [initialLeadId]);

  const search = async () => {
    if (!postcode) return toast.error('Enter a postcode');
    setSearching(true);
    try {
      const res = await fetch(`${API_URL}/api/installers/search-by-postcode`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postcode, section, limit: 15 }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Search failed');
      const data = await res.json();
      setResults(data.results || []);
      if ((data.results || []).length === 0) toast.info('No approved installers in your search area.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSearching(false);
    }
  };

  const offer = async (installer) => {
    if (!leadId) return toast.error('Select a lead first (open via Lead → Find Installer)');
    setOffering(installer.user_id);
    try {
      const res = await fetch(`${API_URL}/api/lead-offers`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_id: leadId, user_id: installer.user_id, section }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Offer failed');
      toast.success(`Offer sent to ${installer.name}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setOffering(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)' }}>
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-4">
          <button onClick={() => navigate(-1)} data-testid="find-installer-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl md:text-2xl font-bold flex items-center gap-2 truncate"><Users className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" /> Find Approved Installer</h1>
            <p className="hidden sm:block text-white/70 text-sm">Search by postcode → offer the lead to the closest Ox Approved installer.</p>
          </div>
          <Button onClick={() => navigate('/')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3" aria-label="Home"><Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Home</span></Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {lead && (
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="text-xs text-blue-700 uppercase tracking-wider font-bold">Offering lead</div>
            <div className="font-bold text-blue-900">{lead.customer_name} · {lead.reference}</div>
            <div className="text-xs text-blue-700">{lead.customer_postcode || lead.customer_address} · {lead.service_type}</div>
          </Card>
        )}

        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_120px] gap-3 items-end">
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-500 font-bold">Customer postcode</label>
              <Input data-testid="find-postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="e.g. OX1 1AA" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-500 font-bold">Section</label>
              <select data-testid="find-section" value={section} onChange={(e) => setSection(e.target.value)} className="w-full border rounded-lg px-3 py-2">
                <option value="probound">ProBound</option>
                <option value="proepoxy">ProEpoxy</option>
                <option value="prodecor">ProDecor</option>
              </select>
            </div>
            <Button data-testid="find-search-btn" onClick={search} disabled={searching} className="bg-slate-800 hover:bg-slate-900 text-white h-10">
              <Search className="w-4 h-4 mr-1" /> {searching ? 'Searching…' : 'Search'}
            </Button>
          </div>
          {!leadId && (
            <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
              ⓘ Open this page from a Lead&apos;s &quot;Find Approved Installer&quot; button to enable lead offers.
            </div>
          )}
        </Card>

        {results.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm text-gray-600">{results.length} approved installers near <strong>{postcode}</strong></div>
            {results.map((r, idx) => (
              <Card key={r.user_id} data-testid={`find-result-${r.user_id}`} className="p-4 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold">#{idx + 1}</div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-gray-900">{r.company_name || r.name}</h3>
                      <ShieldCheck className="w-4 h-4 text-emerald-600" />
                      <span className="text-[10px] font-bold uppercase tracking-wider text-white px-2 py-0.5 rounded-full" style={{ background: SECTION_COLOR[r.section] }}>{SECTION_LABEL[r.section]}</span>
                    </div>
                    <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" /> {r.town || '—'} · <strong>{r.distance_miles} mi</strong>
                    </div>
                  </div>
                </div>
                {leadId && (
                  <Button
                    data-testid={`offer-installer-${r.user_id}`}
                    onClick={() => offer(r)}
                    disabled={offering === r.user_id}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <Send className="w-4 h-4 mr-1" /> {offering === r.user_id ? 'Offering…' : 'Offer Lead'}
                  </Button>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
